import {
  faAlignJustify,
  faAngleLeft,
  faArrowRightFromBracket,
  faArrowUpArrowDown,
  faChevronLeft,
  faCircleInfo,
  faGear,
  faInfoCircle,
  faKey,
  faLocationDot,
  faSearch,
  faStars,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faBadgeCheck,
  faChevronRight,
  faCircle,
  faPencil,
  faPlus,
  faQuestionCircle,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import { filter, get, has, isEmpty, map } from "lodash";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useStoreState } from "pullstate";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProductFruitsApi } from "react-product-fruits";
import useCheckDashboardContainer from "../hooks/useCheckDashboardContainer";

import useCheckWorklistContainer from "../hooks/useCheckWorklistContainer";
import {
  getAppLogo,
  getCurrentRegion,
  getIsOnpremise,
  getIsPatientDeletionEnabled,
  getIsRemoteSystem,
  getUrlAuthToken,
  getUserName,
  isIOSWebView,
  isWebView,
} from "../utils";
import constants from "../utils/constants";
import getEnv from "../utils/getEnv";
import { trackEvent } from "../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../utils/mixpanelEvents";
import Store from "../utils/Store";
import useCheckViewerPage from "../hooks/useCheckViewerPage";
import { LicenseContextType } from "../types/license-context";
import { useAuth } from "@qure/qure-auth";
import useChangeTheme from "./Assistant/utils/actions/useChangeTheme";
import useMenubarActions from "./Assistant/utils/actions/useMenubarActions";
import AwesomeIcon from "./AwesomeIcon";
import Button from "./common/Button";
import Dropdown from "./common/Dropdown";
import DropdownMenu from "./common/DropdownMenu";
import Modal from "./common/Modal";
import Tooltip from "./common/Tooltip";
import UserIcon from "./common/UserIcon";
import { GatewayContext } from "./GatewayIndicator/GatewayContext";
import GatewayDropdown from "./GatewayIndicator/GatewayDropdown";
import { GatewayContextProps } from "./GatewayIndicator/types";
import { LicenseContext } from "./LicenseManager/LicenseContext";
import { PatientsContext } from "./Patients/PatientsContext";
import getPatients from "./Patients/services/getPatients";
import SettingsMenu from "./Settings/SettingsMenu";
import useSettings from "./Settings/useSettings";
import TermsAndConditionsModal from "./TermsAndConditionsModal";

const Menubar = () => {
  useChangeTheme();

  const [open, setOpen] = useState(false);
  const [showSwiftFilter, setShowSwiftFilter] = useState(false);
  const [isLicenseActivated, setLicenseActivated] = useState(false);

  const [isTourEnabled, setEnableTour] = useState(false);
  const { swiftModeEnabled, hasMobileSearch } = useStoreState(Store, (s) => ({
    swiftModeEnabled: s.swiftModeEnabled,
    hasMobileSearch: s.hasMobileSearch,
  }));
  const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();
  const params = get(router, ["query", "params"], []);
  const patient_uid =
    params && params.length ? Number(params?.length > 1 ? params?.[1] : params?.[0]) : "";

  const hideMenubar = !!patient_uid && get(router, ["route"], "").includes("/patients/");
  const isWorkList = useCheckWorklistContainer();
  const isDashboard = useCheckDashboardContainer();
  const isViewer = useCheckViewerPage();
  const { patientsData, setLastId, getFilterParams, filters, count, setFilters, wsData } =
    useContext<any>(PatientsContext);
  const _patientsData = get(patientsData, ["data", "content"], []);
  const _patientsUID = map(_patientsData, "patient_uid");
  const isNotificationSettings = get(router, ["pathname"], "").includes(
    "notification-settings"
  );
  const isGatewayIndicator = get(router, ["pathname"], "").includes("gateway-indicator");
  const isPatientRegistration = get(router, ["pathname"], "").includes(
    "patient-registration"
  );
  const isFormSettings = get(router, ["pathname"], "").includes("form-settings");
  const { offlineGatewayCount, totalGateway } =
    useContext<GatewayContextProps>(GatewayContext);
  const { openSetting } = useSettings();
  const { alertDetails } = useContext<LicenseContextType>(LicenseContext);

  const query = get(router, ["query"], {});
  const onlyViewport = has(query, ["onlyViewport"]);

  const ALLOWED_REGIONS_FOR_PHONE_AUTH = [
    "india-stroke.qure.ai",
    ...constants.TESTING_REGIONS,
  ];

  const isViewerPage =
    ((!isWorkList && !isDashboard) ||
      isNotificationSettings ||
      isGatewayIndicator ||
      isPatientRegistration) &&
    !isFormSettings;

  const isOnpremise = getIsOnpremise();
  const isPatientDeletionEnabled = getIsPatientDeletionEnabled();
  const { userInfo } = useAuth();
  const user = get(userInfo, ["details"], null);
  const isTrialLicense = get(userInfo, ["is_trial"], false);
  const {
    patient,
    isGetFullAccessOpen,
    isSettingsOpen,
    isDraggableModalMinimized,
    worklistSyncStatus,
    isDarkMode,
  } = useStoreState(Store, (store) => ({
    patient: store.patient,
    isGetFullAccessOpen: store.isGetFullAccessOpen,
    currentPatient: store.patientDetail,
    isSettingsOpen: store.isSettingsOpen,
    isStartingDownload: store.isStartingDownload,
    isDraggableModalMinimized: store.isDraggableModalMinimized,
    worklistSyncStatus: store.worklistSyncStatus,
    isDarkMode: store.isDarkMode,
  }));
  const metabase_enabled = get(userInfo, ["metabase_enabled"], false);
  const phoneNumber = get(userInfo, ["phone_number"], null);
  const diskUsage = get(userInfo, ["disk_usage"], {});
  const isLoggedIn = get(userInfo, ["authenticated"], false);
  const activePatientDetail =
    filter(_patientsData, ["patient_uid", patient_uid])[0] ?? {};
  const page_size = get(patientsData, ["data", "page_size"]);
  const indexOfCurrentPatient = _patientsUID.indexOf(patient_uid);
  const hasPatientDetails = !isEmpty(activePatientDetail) || !isEmpty(patient);
  const urlAuthToken = getUrlAuthToken();
  const DiskAlertbuttonRef = useRef<HTMLButtonElement>(null);
  const LicenseAlertbuttonRef = useRef<HTMLButtonElement>(null);
  const LicenseActivateRef = useRef<any>(null);

  const logo = getAppLogo();

  useProductFruitsApi(
    (api) => {
      if (getEnv("NEXT_PUBLIC_PRODUCTFRUITS_TOUR_ID") && isTourEnabled) {
        api.tours.tryStartTour(getEnv("NEXT_PUBLIC_PRODUCTFRUITS_TOUR_ID"));
        api.tours.listen("tour-finished", onTourFinished);
        api.tours.listen("tour-skipped", onTourFinished);
      }
    },
    [isTourEnabled, getEnv("NEXT_PUBLIC_PRODUCTFRUITS_TOUR_ID")]
  );

  const onTourFinished = () => {
    setEnableTour(false);
  };

  useEffect(() => {
    if (localStorage.getItem(constants.PERSISTED.isSwiftReportModeEnabled)) {
      Store.update((s) => {
        s.swiftModeEnabled = true;
      });
    } else {
      Store.update((s) => {
        s.swiftModeEnabled = false;
      });
    }
  }, []);

  const handleLicenseAlert = () => {
    setTimeout(() => {
      setLicenseActivated(false);
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        LicenseActivateRef.current &&
        !LicenseActivateRef?.current?.contains(event.target)
      ) {
        handleLicenseAlert();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (indexOfCurrentPatient === _patientsUID.length - 2 && page_size === 10) {
      setLastId(_patientsUID[_patientsUID.length - 1]);
    }
  }, [indexOfCurrentPatient]);

  const handleChangeButton = (type: "next" | "prev") => {
    if (indexOfCurrentPatient !== -1) {
      if (type === "next" && indexOfCurrentPatient !== _patientsData.length) {
        router.replace(`/viewer/quredicom/${_patientsUID[indexOfCurrentPatient + 1]}`);
      } else if (indexOfCurrentPatient !== 0) {
        router.replace(`/viewer/quredicom/${_patientsUID[indexOfCurrentPatient - 1]}`);
      }
      trackEvent(MIXPANEL_EVENTS.VIEWER_HEADER_NAVIGATION, {
        type,
      });
    } else {
      const filterParams = getFilterParams(filters);
      const params = {
        page_size: 1,
        ...filterParams,
        pagination_id: get(router, ["query", "params", 1]),
        page: type,
      };
      getPatients(params)
        .then((_patientsData) => {
          router.replace(`/viewer/quredicom/${get(_patientsData, ["data", "first_id"])}`);
          trackEvent(MIXPANEL_EVENTS.VIEWER_HEADER_NAVIGATION, {
            type,
          });
        })
        .catch((e) => {
          console.error("err", e);
        });
    }
  };

  const onBack = () => {
    if (isIOSWebView()) {
      router.back();
    } else {
      router.replace(`/patients/${patient_uid}`);
    }
    trackEvent(MIXPANEL_EVENTS.EXIT_VIEWER);
    Store.update((s) => {
      s.swiftModeEnabled = false;
    });
    localStorage.removeItem(constants.PERSISTED.isSwiftReportModeEnabled);
    if (swiftModeEnabled) {
      setFilters(constants.DEEFAULT_FILTER);
    }
  };

  useMenubarActions(onBack);
  useEffect(() => {
    if (userInfo?.patients_export) {
      Store.update((s) => {
        s.isExportReady = userInfo?.patients_export?.available;
        s.exportTimestamp = userInfo?.patients_export?.timestamp;
        s.downloadProgress = userInfo?.patients_export?.progress;
        s.exportGenFailed = userInfo?.patients_export?.failed;
      });
    }
  }, [userInfo]);

  const AddPhoneNumber = () => {
    return (
      <div
        className={classNames({
          hidden:
            isOnpremise ||
            !ALLOWED_REGIONS_FOR_PHONE_AUTH.some((region) =>
              window?.location?.host.includes(region)
            ),
        })}
        onClick={() => {
          Store.update((s) => {
            s.addPhoneModal = true;
          });
          let event = MIXPANEL_EVENTS.OPEN_ADD_PHONE_NUMBER;
          if (!isEmpty(phoneNumber)) {
            event = MIXPANEL_EVENTS.OPEN_UPDATE_PHONE_NUMBER;
          }
          trackEvent(event);
        }}
      >
        {!isEmpty(phoneNumber) ? (
          <div className="!cursor-pointer text-md text-gray-400 break-all">
            {phoneNumber}{" "}
            <AwesomeIcon name={faPencil} size="lg" className="w-4 ml-2 text-teal-400" />
          </div>
        ) : (
          <div className="text-md text-teal-400 !cursor-pointer hover:underline">
            <AwesomeIcon name={faPlus} className="hidden w-4 mr-2 text-teal-400" />
            {`${t("add_phone.link")} ${t("phone_number")}`}
          </div>
        )}
      </div>
    );
  };
  useEffect(() => {
    const _showDiskAlert = diskUsage && [75, 80, 85].includes(diskUsage?.used_percent);

    if (_showDiskAlert) {
      if (DiskAlertbuttonRef.current) {
        DiskAlertbuttonRef?.current?.click();
      }
    }
    if (diskUsage?.used_percent > 90) {
      Store.update((s) => {
        s.isDiskSpaceWarning = true;
      });
    }
  }, [diskUsage]);

  const triggerButtonClick = () => {
    if (LicenseAlertbuttonRef?.current) {
      LicenseAlertbuttonRef.current.click();
    }
  };

  useEffect(() => {
    if (alertDetails?.isPopupVisible) {
      setTimeout(() => {
        triggerButtonClick();
      }, 800);
    }
  }, [alertDetails]);

  useEffect(() => {
    if (wsData?.action === "license_renewed") {
      triggerButtonClick();
      setLicenseActivated(true);
    }
  }, [wsData]);

  const onDismissLicenseALert = () => {
    if (!isEmpty(alertDetails)) {
      const {
        app_name,
        license_type,
        total_scans_remaining,
        total_scans,
        total_days_remaining,
        total_days,
        remainingPercentage,
      } = alertDetails;
      let params: {
        app_name?: string;
        license_type?: string;
        total_scans_remaining?: number;
        total_scans?: number;
        total_days_remaining?: number;
        total_days?: number;
        remainingPercentage?: number;
      } = {
        app_name,
        license_type,
        remainingPercentage,
      };
      if (license_type === "TIME") {
        params = { ...params, total_days_remaining, total_days };
      } else if (license_type === "CONT") {
        params = { ...params, total_scans_remaining, total_scans };
      }
      localStorage.setItem("licenseAlertDismissed", JSON.stringify(params));
    }
  };

  const diskAlertPrompt = [
    {
      name: (
        <div className="flex flex-col p-4 bg-gray-100 overflow-hidden rounded-t text-gray-700">
          <p className="text-gray-900 text-lg">
            <AwesomeIcon
              name={faTriangleExclamation}
              size="xl"
              className="text-yellow-500"
            />{" "}
            {t("disk_management.x_storage_full", {
              usedPercent: diskUsage?.used_percent,
            })}
          </p>
          <div className="py-4">
            {t("disk_management.alert", {
              usedPercent: diskUsage?.used_percent,
              totalDisk: diskUsage?.total,
            })}
          </div>
          <div className="flex justify-end">
            <div
              className="grow-1 px-4 py-3 text-gray-500"
              onClick={() => {
                trackEvent(MIXPANEL_EVENTS.DISK_SPACE_ALERT_DISMISSED);
              }}
            >
              {t("button.dismiss")}
            </div>
            <div
              onClick={() => {
                Store.update((s) => {
                  s.isSettingsOpen = true;
                  s.selectedSetting = "disk-management";
                });
                setOpen(true);
                trackEvent(MIXPANEL_EVENTS.DISK_SPACE_ALERT_MANAGE_STORAGE_CLICKED);
              }}
              className="grow-1 px-4 py-3 text-teal-500"
            >
              {t("disk_management.manage_storage")} {">"}
            </div>
          </div>
        </div>
      ),
      onClick: () => {},
    },
  ];

  const licenseAlertPrompt = [
    {
      name: (
        <div
          className={classNames(
            "flex flex-col p-4 bg-gray-100 overflow-hidden rounded-t text-gray-700 cursor-default",
            {
              hidden:
                !get(alertDetails, ["isAlert"], false) ||
                isLicenseActivated ||
                !!alertDetails?.isExpired ||
                isTrialLicense,
            }
          )}
        >
          <p className="text-gray-900 text-lg">
            <AwesomeIcon
              name={faTriangleExclamation}
              size="xl"
              className="text-yellow-500"
            />{" "}
            {alertDetails?.type === "days"
              ? `${alertDetails?.total_days_remaining} ${t("license_manager.days")}`
              : `${alertDetails?.total_scans_remaining} ${t(
                  "license_manager.scans"
                )}`}{" "}
            {t("left_key")}
          </p>
          <div className="py-4">
            {alertDetails?.type === "days"
              ? t("license_manager.license_expire_in_alert").replace(
                  "[1]",
                  `${alertDetails?.total_days_remaining}`
                )
              : t("license_manager.license_expire_after_alert").replace(
                  "[1]",
                  `${alertDetails?.total_scans_remaining}`
                )}
          </div>
          <div className="flex justify-end">
            <div
              className="grow-1 px-4 py-3 text-gray-500 cursor-pointer"
              onClick={() => {
                onDismissLicenseALert();
                trackEvent(MIXPANEL_EVENTS.LICENSE_ALERT_DISMISSED);
              }}
            >
              {t("button.dismiss")}
            </div>
            <div
              onClick={() => {
                Store.update((s) => {
                  s.isSettingsOpen = true;
                  s.selectedSetting = "license";
                });
                setOpen(true);
                trackEvent(MIXPANEL_EVENTS.LICENSE_ALERT_VIEW_DETAILS_CLICKED);
              }}
              className="grow-1 px-4 py-3 text-teal-500 cursor-pointer"
            >
              {t("view_details")} {">"}
            </div>
          </div>
        </div>
      ),
      onClick: () => {},
    },
    {
      name: (
        <div
          className={classNames(
            "flex flex-col p-4 bg-gray-100 overflow-hidden rounded-t text-gray-700 cursor-default",
            {
              hidden:
                (get(alertDetails, ["isAlert"], false) && !isLicenseActivated) ||
                isTrialLicense,
            }
          )}
          ref={LicenseActivateRef}
        >
          <p className="text-gray-900 text-lg">
            <AwesomeIcon name={faBadgeCheck} size="xl" className="text-green-500" />{" "}
            {t("license_manager.license_activated")}
          </p>
          <div className="py-4">{t("license_manager.your_license_activated")}</div>
          <div className="flex justify-end">
            <div
              onClick={handleLicenseAlert}
              className="grow-1 px-4 py-3 text-gray-500 cursor-pointer"
            >
              {t("button.dismiss")}
            </div>
            <div
              onClick={() => {
                Store.update((s) => {
                  s.isSettingsOpen = true;
                  s.selectedSetting = "license";
                });
                setOpen(true);
                handleLicenseAlert();
              }}
              className="grow-1 px-4 py-3 text-teal-500 !cursor-pointer"
            >
              {t("view_details")} {">"}
            </div>
          </div>
        </div>
      ),
      onClick: () => handleLicenseAlert(),
    },
  ];

  const profileNavigations = [
    {
      name: (
        <div className="flex items-center justify-start p-4 bg-gray-700 overflow-hidden rounded-t text-white">
          <div>
            <UserIcon name={getUserName(user)} className="h-8 w-8 text-lg" />
          </div>
          <div className="pl-4 flex flex-col items-start justify-center">
            <div className="font-medium text-base break-all">{getUserName(user)}</div>
            <div className="text-md text-gray-400 break-all">
              {get(user, ["email"], "")}
            </div>
            <AddPhoneNumber />
          </div>
        </div>
      ),
      onClick: () => {},
    },
    {
      name: (
        <div className="flex p-4 items-center justify-start gap-3">
          <AwesomeIcon name={faInfoCircle} />
          <p>{t("settings.terms-of-use")}</p>
        </div>
      ),
      onClick: () => {
        setShowTermsAndConditionsModal(true);
        trackEvent(MIXPANEL_EVENTS.OPEN_TERMS_OF_USE);
      },
    },
    {
      name: (
        <div className="flex p-4 items-center justify-start gap-3">
          <AwesomeIcon name={faLocationDot} />
          <p>{t(getCurrentRegion())}</p>
        </div>
      ),
      className: "!text-gray-800 dark:!text-gray-200 !cursor-default !bg-transparent",
      onClick: () => {},
    },
    {
      name: (
        <div className="flex p-4 items-center justify-start">
          <div className="h-6 w-6 flex items-center justify-center">
            <AwesomeIcon name={faArrowRightFromBracket} size="lg" />
          </div>

          <div className="flex items-center justify-start pl-3">{t("logout_key")}</div>
        </div>
      ),
      onClick: () =>
        Store.update((s) => {
          s.isLogoutOpen = true;
        }),
    },
  ];

  const toggleProductButton = () => {
    setEnableTour(true);
  };

  // Add keyboard shortcut handler
  useEffect(() => {
    const isAIAssistantEnabled = get(
      userInfo,
      ["settings", "ui_settings", "ai_assistant"],
      false
    );

    if (!isAIAssistantEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for CMD+/ (Mac) or CTRL+/ (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        e.preventDefault(); // Prevent default browser behavior
        Store.update((s) => {
          s.isDraggableModalOpen = !s.isDraggableModalOpen;
          s.isDraggableModalMinimized = false;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [userInfo]);

  if (!onlyViewport && urlAuthToken && !isLoggedIn) {
    const onClose = () => {
      Store.update((s) => {
        s.isGetFullAccessOpen = false;
      });
    };
    const onOpen = () => {
      Store.update((s) => {
        s.isGetFullAccessOpen = true;
      });
    };
    return (
      <header
        className={classNames(
          "flex items-center bg-gray-900 px-5 py-4 border-b border-gray-800",
          {
            "pt-8": isWebView(),
          }
        )}
      >
        <Link href="/">
          <Image
            className="block h-auto w-auto cursor-pointer"
            src={logo}
            alt="Qure.ai"
            width="76"
            height="25"
            priority
          />
        </Link>
        <button className="bg-teal-500 ml-auto py-3 px-5 rounded-lg" onClick={onOpen}>
          <span className="text-gray-200">{t("full_access")}</span>
        </button>

        <Modal
          show={isGetFullAccessOpen}
          onClose={onClose}
          title="Get Full Access"
          className="w-full sm:w-9/12 md:w-8/12 lg:w-6/12 xl:w-5/12"
          closeOnClickAway
        >
          <div className="p-5">
            <div className="text-lg text-gray-800 dark:text-gray-200 py-3">
              {t("add_worklist_signin_access")}
            </div>
            <div className="text-gray-100 text-base space-x-5">
              <Button onClick={onClose} className="grow-1 px-6 py-4">
                {t("no_thanks")}
              </Button>
              <Button
                isPrimary
                onClick={() => {
                  router.push("/account/login");
                }}
                className="grow-1 px-6 py-4"
              >
                {t("add_worklist")}
              </Button>
            </div>
          </div>
        </Modal>
      </header>
    );
  }

  const tabsOptions = [
    {
      id: t("worklist_key"),
      label: t("worklist_key"),
      value: "/patients",
    },
    {
      id: t("dashboard_key"),
      label: t("dashboard_key"),
      value: "/dashboard",
    },
  ];

  let selectedTab =
    tabsOptions.find((opt) => opt.value === window.location.pathname) || tabsOptions[0];

  const diskUsageThreshold = parseInt(
    getEnv("NEXT_PUBLIC_DISK_USAGE_THRESHOLD") || "95",
    10
  );
  const disableAddPatient = isOnpremise && diskUsage?.used_percent >= diskUsageThreshold;

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 flex text-gray-200">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex flex-col w-full overflow-y-auto bg-gray-800 shadow-xl">
                <SettingsMenu
                  onLogout={() => {
                    Store.update((s) => {
                      s.isLogoutOpen = true;
                    });
                  }}
                  onClose={() => setOpen(false)}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <header
        className={classNames(
          "bg-gray-900 shadow-sm lg:static lg:overflow-y-visible h-fit border-b-none sm:border-b border-gray-800",
          {
            "hidden sm:!block": hideMenubar,
            "pt-7": isWebView(),
            "!bg-gray-800 sm:!bg-gray-900": isWorkList,
          }
        )}
      >
        {!hasMobileSearch && (
          <div className="mx-0">
            <div className="flex justify-between items-center">
              <div
                className={classNames("relative flex sm:justify-between sm:w-full", {
                  "w-full": isWorkList || isDashboard,
                })}
              >
                {/* mobile settings sidebar CTA */}
                {!(
                  isNotificationSettings ||
                  isGatewayIndicator ||
                  isPatientRegistration ||
                  isFormSettings
                ) && (
                  <div className="flex items-center sm:hidden inset-y-0">
                    <button
                      onClick={() => {
                        setOpen(true);
                        trackEvent(MIXPANEL_EVENTS.OPEN_SETTINGS);
                      }}
                      className="-mx-2 rounded-md p-5 inline-flex items-center justify-center text-gray-200"
                    >
                      <span className="sr-only">{t("open_menu")}</span>
                      <AwesomeIcon name={faAlignJustify} size="lg" className="w-6" />
                    </button>
                  </div>
                )}

                <div className="flex md:left-0 md:inset-y-0 lg:static xl:col-span-2 w-full sm:w-auto justify-center items-center sm:items-stretch">
                  <div className="flex flex-shrink-0">
                    <span className="hidden sm:flex justify-center items-center ml-4 cursor-pointer shrink-0">
                      <Link href="/">
                        <Image
                          className="block h-auto w-auto"
                          src={logo}
                          alt="Qure.ai"
                          width="76"
                          height="25"
                          priority
                        />
                      </Link>
                    </span>

                    {/* Dashboard <-> Worklist toggle */}
                    {(isDashboard || isWorkList) && metabase_enabled && (
                      <>
                        <div className="hidden sm:flex">
                          <div
                            className={`text-gray-100 flex items-center whitespace-nowrap ${
                              router.asPath === "/dashboard"
                                ? "bg-teal-500/25 border-b-4 border-teal-400"
                                : "border-b-4 border-gray-900 hover:border-teal-400/10 "
                            }
                                 transition duration-500
                                 hover:bg-teal-500/10 ml-6 px-4`}
                          >
                            <Link href="/dashboard">{t("dashboard_key")}</Link>
                          </div>
                          <div
                            className={`text-gray-100 text-nowrap flex items-center ${
                              /patients/g.test(router.asPath)
                                ? "bg-teal-500/25 border-b-4 border-teal-400"
                                : "border-b-4 border-gray-900 hover:border-teal-400/10"
                            }
                               transition duration-500 hover:bg-teal-500/10 px-4`}
                          >
                            <Link href="/patients">{t("worklist_key")}</Link>
                          </div>
                        </div>
                        <Dropdown
                          options={tabsOptions}
                          isBottomAligned
                          onChange={(opt: { value: string }) => {
                            router.push(opt.value, undefined, { shallow: true });
                          }}
                          selectedOption={selectedTab}
                          className={classNames("flex sm:hidden font-medium w-full", {
                            hidden: getIsRemoteSystem(),
                          })}
                          iconClassName="text-md text-teal-300"
                          parentClassName="z-50"
                        />
                      </>
                    )}

                    {isWorkList && !metabase_enabled && (
                      <p className="text-gray-100 flex items-center sm:hidden">
                        {t("worklist_key")}
                      </p>
                    )}

                    {isViewerPage && (
                      <span
                        onClick={onBack}
                        className={classNames(
                          "flex justify-center items-center text-gray-200 cursor-pointer p-5",
                          {
                            "ml-8 border-l border-gray-800":
                              !isWorkList &&
                              !isNotificationSettings &&
                              !isGatewayIndicator &&
                              !isFormSettings &&
                              !isPatientRegistration,
                          }
                        )}
                      >
                        <AwesomeIcon name={faAngleLeft} className="ml-3 w-5 mr-3" />
                        {t("back_key")}
                      </span>
                    )}
                  </div>
                </div>

                {/* viewer left - right arrows to switch scans */}
                <div
                  className={classNames("hidden items-center text-white md:space-x-8", {
                    "!flex": isViewer,
                  })}
                >
                  <Tooltip
                    tooltipContainerStyles={"cursor-pointer"}
                    tooltipText={
                      <div>
                        <p>{t("previous_patient")}</p>
                      </div>
                    }
                    position="right"
                    verticalPosition="bottom"
                    tooltipStyles="text-sm rounded-6 p-3 px-4 text-gray-100"
                    tooltipBg="bg-gray-700"
                  >
                    <button
                      disabled={
                        (indexOfCurrentPatient === 0 || isEmpty(activePatientDetail)) &&
                        indexOfCurrentPatient !== -1
                      }
                      onClick={() => {
                        handleChangeButton("prev");
                      }}
                      className="disabled:opacity-50 hidden md:items-center disabled:hover:bg-transparent md:hover:bg-gray-700 md:rounded-6 md:flex md:w-7 md:h-7 md:justify-center"
                    >
                      <AwesomeIcon
                        name={faChevronLeft}
                        className={classNames("text-white hidden stroke-2", {
                          "!block": isViewerPage && hasPatientDetails,
                        })}
                      />
                    </button>
                  </Tooltip>

                  <div className="items-center text-gray-200 flex">
                    {hasPatientDetails &&
                      !isWorkList &&
                      !isDashboard &&
                      !isNotificationSettings &&
                      !isGatewayIndicator &&
                      !isFormSettings &&
                      !isPatientRegistration && (
                        <span className="break-all">
                          {activePatientDetail?.name ?? patient?.name} |{" "}
                          <span
                            title={activePatientDetail?.patient_id ?? patient?.patientID}
                          >
                            {(
                              activePatientDetail?.patient_id ?? patient?.patientID
                            )?.slice(0, 10)}
                          </span>
                        </span>
                      )}
                  </div>
                  <Tooltip
                    tooltipContainerStyles={"cursor-pointer"}
                    tooltipText={
                      <div>
                        <p>{t("next_patient")}</p>
                      </div>
                    }
                    position="left"
                    verticalPosition="bottom"
                    tooltipStyles="text-sm rounded-6 p-3 px-4 text-gray-100"
                    tooltipBg="bg-gray-700"
                  >
                    <button
                      disabled={
                        (indexOfCurrentPatient === _patientsUID.length - 1 ||
                          isEmpty(activePatientDetail)) &&
                        indexOfCurrentPatient !== -1
                      }
                      className="disabled:opacity-50 hidden md:items-center md:hover:bg-gray-700 md:rounded-6 md:flex md:w-7 md:h-7 md:justify-center"
                      onClick={() => {
                        handleChangeButton("next");
                      }}
                    >
                      <AwesomeIcon
                        name={faChevronRight}
                        className={classNames("text-white hidden stroke-2", {
                          "!block": isViewerPage && hasPatientDetails,
                        })}
                      />
                    </button>
                  </Tooltip>
                </div>
                {/* Swift reporting mode filter */}
                {swiftModeEnabled && isViewerPage && (
                  <div className="border-teal-700 group z-50 relative h-fit my-auto flex items-center border rounded-lg bg-gray-800">
                    <div className="bg-teal-800 py-3 pr-5 pl-2 border-teal-700 border-r rounded-l-lg flex items-center space-x-4">
                      <svg
                        onClick={() => {
                          setShowSwiftFilter(!showSwiftFilter);
                        }}
                        className="text-teal-500 dark:text-gray-400 w-6 h-6 cursor-pointer"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.1406 4.5C15.0273 4.5 15.75 5.22109 15.75 6.10937C15.75 6.51562 15.5977 6.91016 15.2852 7.20703L11.6523 11.1172V18.2266C11.6523 18.9297 11.1172 19.5 10.4141 19.5C10.1289 19.5 9.91797 19.4258 9.71094 19.2891L6.73047 17.3438C6.32422 17.0547 6.0625 16.5625 6.0625 16.0391V11.1172L2.43047 7.20703C2.15375 6.91016 2 6.51562 2 6.10937C2 5.22109 2.72109 4.5 3.61055 4.5H14.1406ZM7.90234 10.75V15.8711L9.77734 17.1094V10.75C9.77734 10.5117 9.90234 10.2852 10.0625 10.1133L13.5312 6.375H4.2168L7.6875 10.1133C7.84766 10.2852 7.90234 10.5117 7.90234 10.75ZM21.0625 17.3125C21.582 17.3125 22 17.7305 22 18.25C22 18.7695 21.582 19.1875 21.0625 19.1875H15.4375C14.918 19.1875 14.5 18.7695 14.5 18.25C14.5 17.7305 14.918 17.3125 15.4375 17.3125H21.0625ZM14.5 12C14.5 11.4805 14.918 11.0625 15.4375 11.0625H21.0625C21.582 11.0625 22 11.4805 22 12C22 12.5195 21.582 12.9375 21.0625 12.9375H15.4375C14.918 12.9375 14.5 12.5195 14.5 12ZM21.0625 4.8125C21.582 4.8125 22 5.23242 22 5.75C22 6.26953 21.582 6.6875 21.0625 6.6875H17.9375C17.418 6.6875 17 6.26953 17 5.75C17 5.23242 17.418 4.8125 17.9375 4.8125H21.0625Z"
                          className="fill-gray-500 dark:fill-gray-400"
                        />
                      </svg>
                      <p className="font-semibold text-teal-300 ">
                        {indexOfCurrentPatient + 1} of {count}
                      </p>
                    </div>
                    <p className="text-white font-medium px-5 py-3">
                      {t("swift_report_mode")}
                    </p>
                    <button
                      title={t("exit_swift_reporting_mode")}
                      onClick={() => {
                        onBack();
                      }}
                      className="bg-red-700 invisible group-hover:visible border border-gray-600 rounded-r-lg px-3 py-3"
                    >
                      <AwesomeIcon
                        name={faXmark}
                        className="w-6 h-6 text-gray-100 stroke-2"
                      />
                    </button>

                    {showSwiftFilter && (
                      <div className="absolute translate-y-[75%] z-50 bg-gray-800 border text-gray-200 gap-4 rounded-xl border-gray-700 flex flex-wrap p-5">
                        <p className="bg-gray-700 px-4 py-2 rounded-full">
                          {t("negative")}
                        </p>
                        <p className="bg-gray-700 px-4 py-2 rounded-full">
                          {t("screened_by_qure")}
                        </p>
                        <p className="bg-gray-700 px-4 py-2 rounded-full">CXR</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="hidden sm:flex sm:items-center sm:justify-end xl:col-span-4">
                  {get(userInfo, ["settings", "ui_settings", "ai_assistant"], false) && (
                    <button
                      className={classNames(
                        "inline-flex items-center justify-center text-base font-medium rounded-6 " +
                          "text-gray-100 !px-4 !py-2 mr-6 border border-brand-peach hover:opacity-80 cursor-pointer user-select-none"
                      )}
                      data-testid="assistant-button"
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.ASSISTANT_OPEN);
                        if (isDraggableModalMinimized) {
                          Store.update((s) => {
                            s.isDraggableModalMinimized = false;
                          });
                        } else {
                          Store.update((s) => {
                            s.isDraggableModalOpen = !s.isDraggableModalOpen;
                            s.isDraggableModalMinimized = false;
                          });
                        }
                      }}
                    >
                      <AwesomeIcon
                        label="aiAssitant"
                        name={faStars}
                        size="lg"
                        className="mr-3 text-brand-peach "
                      />
                      <span className="hidden sm:inline">AI Assistant</span>
                    </button>
                  )}
                  {/* Patient registration / Scan upload CTA */}
                  {isWorkList && !disableAddPatient && (
                    <button
                      className={classNames(
                        "inline-flex items-center justify-center text-base font-medium rounded-6 text-gray-100 !px-5 !py-3 mr-3 bg-teal-800 hover:bg-teal-900"
                      )}
                      data-testid="add-patient-button"
                      onClick={() =>
                        Store.update((s) => {
                          s.uploadModal = {
                            isOpen: true,
                            hasCurrentPatient: false,
                          };
                        })
                      }
                    >
                      {t("add_key")}{" "}
                    </button>
                  )}
                  {/* Gateway indicator status */}
                  {totalGateway > 0 && (
                    <DropdownMenu
                      options={[
                        {
                          name: (
                            <div className="flex p-4 items-center justify-start hover:!bg-teal-500 dark:hover:!bg-teal-800 border-t border-gray-300 dark:border-gray-500/40">
                              <AwesomeIcon
                                name={faCircleInfo}
                                size="lg"
                                className="w-6"
                              />
                              <div className="flex items-center justify-start ml-3">
                                {t("more_information")}
                              </div>
                            </div>
                          ),
                          onClick: () => openSetting(constants.SETTINGS.GATEWAY),
                        },
                      ]}
                      menuItemClassName="z-50 w-80"
                      childrenContent={<GatewayDropdown />}
                      Icon={() => (
                        <div className="relative">
                          {offlineGatewayCount > 0 && (
                            <div className="absolute border-2 text-gray-100 border-gray-900 flex justify-center items-center text-sm rounded-full bg-red-500 right-4 font-medium top-3 w-5 h-5">
                              {offlineGatewayCount}
                            </div>
                          )}
                          <AwesomeIcon
                            name={faArrowUpArrowDown}
                            size="lg"
                            className="m-5 text-gray-200"
                          />
                        </div>
                      )}
                    />
                  )}
                  {/* License Alert CTA */}
                  <DropdownMenu
                    refer={LicenseAlertbuttonRef}
                    options={licenseAlertPrompt}
                    menuItemClassName="z-50 w-80"
                    Icon={() => (
                      <>
                        {alertDetails?.isAlert && !isTrialLicense ? (
                          <div
                            className="relative"
                            onClick={(e) => {
                              if (alertDetails.isExpired) {
                                e.stopPropagation();
                                Store.update((s) => {
                                  s.isSettingsOpen = true;
                                  s.selectedSetting = "license";
                                });
                              }
                            }}
                          >
                            <div className="absolute -right-3 -top-3">
                              <AwesomeIcon
                                name={faTriangleExclamation}
                                size="sm"
                                className="m-5 text-yellow-500"
                              />
                            </div>
                            <AwesomeIcon
                              name={faKey}
                              size="lg"
                              className="m-5 text-gray-200"
                            />
                          </div>
                        ) : (
                          <div className="h-8"></div>
                        )}
                      </>
                    )}
                  />
                  {/* Settings CTA */}
                  <button
                    className={classNames(
                      "flex group text-gray-200 border-b-2 relative z-10 border-transparent hover:text-gray-100 hover:bg-teal-900",
                      {
                        "bg-teal-900 !text-gray-100 !border-teal-500": isSettingsOpen,
                      }
                    )}
                    data-testid="settings-button"
                    onClick={() => {
                      Store.update((s) => {
                        s.isSettingsOpen = true;
                      });
                      trackEvent(MIXPANEL_EVENTS.OPEN_SETTINGS);
                    }}
                  >
                    <AwesomeIcon
                      label="settings"
                      name={faGear}
                      size="lg"
                      className="m-5"
                    />
                  </button>
                  {/* Disk Alert CTA */}
                  {isOnpremise &&
                    isPatientDeletionEnabled &&
                    diskUsage?.used_percent > 75 && (
                      <DropdownMenu
                        refer={DiskAlertbuttonRef}
                        options={diskAlertPrompt}
                        menuItemClassName="z-50"
                        Icon={() => (
                          <svg
                            width="56"
                            height="64"
                            viewBox="0 0 56 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M19 23.75C18.5875 23.75 18.25 24.0875 18.25 24.5V32.0938C18.4891 32.0328 18.7422 32 19 32H37C37.2578 32 37.5109 32.0328 37.75 32.0938V24.5C37.75 24.0875 37.4125 23.75 37 23.75H19ZM18.25 35V39.5C18.25 39.9125 18.5875 40.25 19 40.25H37C37.4125 40.25 37.75 39.9125 37.75 39.5V35C37.75 34.5875 37.4125 34.25 37 34.25H19C18.5875 34.25 18.25 34.5875 18.25 35ZM16 35V24.5C16 22.8453 17.3453 21.5 19 21.5H37C38.6547 21.5 40 22.8453 40 24.5V35V39.5C40 41.1547 38.6547 42.5 37 42.5H19C17.3453 42.5 16 41.1547 16 39.5V35ZM29.125 37.25C29.125 36.9516 29.2435 36.6655 29.4545 36.4545C29.6655 36.2435 29.9516 36.125 30.25 36.125C30.5484 36.125 30.8345 36.2435 31.0455 36.4545C31.2565 36.6655 31.375 36.9516 31.375 37.25C31.375 37.5484 31.2565 37.8345 31.0455 38.0455C30.8345 38.2565 30.5484 38.375 30.25 38.375C29.9516 38.375 29.6655 38.2565 29.4545 38.0455C29.2435 37.8345 29.125 37.5484 29.125 37.25ZM34.75 36.125C35.0484 36.125 35.3345 36.2435 35.5455 36.4545C35.7565 36.6655 35.875 36.9516 35.875 37.25C35.875 37.5484 35.7565 37.8345 35.5455 38.0455C35.3345 38.2565 35.0484 38.375 34.75 38.375C34.4516 38.375 34.1655 38.2565 33.9545 38.0455C33.7435 37.8345 33.625 37.5484 33.625 37.25C33.625 36.9516 33.7435 36.6655 33.9545 36.4545C34.1655 36.2435 34.4516 36.125 34.75 36.125Z"
                              fill="#D9DBDD"
                            />
                            <g clipPath="url(#clip0_5582_11554)">
                              <path
                                d="M41.9995 11.25C42.5542 11.25 43.0659 11.543 43.3472 12.0234L51.7847 26.3984C52.0698 26.8828 52.0698 27.4805 51.7925 27.9648C51.5152 28.4492 50.9956 28.75 50.437 28.75H33.562C33.0034 28.75 32.4839 28.4492 32.2066 27.9648C31.9292 27.4805 31.9331 26.8789 32.2144 26.3984L40.6519 12.0234C40.9331 11.543 41.4448 11.25 41.9995 11.25ZM41.9995 16.25C41.48 16.25 41.062 16.668 41.062 17.1875V21.5625C41.062 22.082 41.48 22.5 41.9995 22.5C42.5191 22.5 42.937 22.082 42.937 21.5625V17.1875C42.937 16.668 42.5191 16.25 41.9995 16.25ZM43.2495 25C43.2495 24.6685 43.1178 24.3505 42.8834 24.1161C42.649 23.8817 42.3311 23.75 41.9995 23.75C41.668 23.75 41.3501 23.8817 41.1157 24.1161C40.8812 24.3505 40.7495 24.6685 40.7495 25C40.7495 25.3315 40.8812 25.6495 41.1157 25.8839C41.3501 26.1183 41.668 26.25 41.9995 26.25C42.3311 26.25 42.649 26.1183 42.8834 25.8839C43.1178 25.6495 43.2495 25.3315 43.2495 25Z"
                                fill="#FFA000"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_5582_11554">
                                <rect
                                  width="20"
                                  height="20"
                                  fill="white"
                                  transform="translate(32 10)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        )}
                      />
                    )}
                  {/* Product fruits tour CTA */}
                  <button
                    onClick={toggleProductButton}
                    className={classNames(
                      "hover:bg-teal-900 h-full px-5 border-b-2 border-gray-900",
                      {
                        hidden: !getEnv("NEXT_PUBLIC_PRODUCTFRUITS_TOUR_ID"),
                      }
                    )}
                  >
                    <AwesomeIcon name={faQuestionCircle} className="text-gray-300" />
                  </button>
                  {/* User Profile Dropdown */}
                  {isLoggedIn && (
                    <DropdownMenu
                      options={profileNavigations}
                      menuItemClassName="z-50"
                      Icon={({ open }) => (
                        <UserIcon
                          className={classNames("h-7 w-7 m-4 group-hover:bg-gray-700", {
                            "!bg-gray-700": open,
                          })}
                          name={getUserName(user)}
                        />
                      )}
                      dataTestId="user-profile-dropdown"
                    />
                  )}
                </div>
              </div>

              {/* Navigate to Filters CTA for mobile */}
              <div className="p-5 sm:hidden flex justify-end items-center">
                <button
                  onClick={() => {
                    Store.update((s) => {
                      s.hasMobileSearch = true;
                    });
                  }}
                  className={classNames("relative text-white", {
                    hidden: !isWorkList,
                  })}
                >
                  {filters?.isFilterApplied && (
                    <AwesomeIcon
                      name={faCircle}
                      size="2xs"
                      className="absolute top-0 right-1 text-teal-400 p-1 bg-gray-800 rounded-full"
                    />
                  )}
                  <AwesomeIcon name={faSearch} className="w-6" />
                </button>
              </div>
            </div>
          </div>
        )}

        {showTermsAndConditionsModal && (
          <TermsAndConditionsModal
            onClose={() => {
              setShowTermsAndConditionsModal(false);
            }}
            showModal={showTermsAndConditionsModal}
            // closeOnClickAway
            mode="readonly"
          />
        )}
      </header>
    </>
  );
};
export default Menubar;
